// src/pages/locations/locations.access.ts
import { getAuth } from "@/api/client";

/**
 * Locations is ONE admin module made of three related datasets. The backend
 * guards every locations write with requirePermission("locations"), so that
 * slug is the source of truth. The finer-grained slugs are kept as an
 * alternative grant so existing roles that were given only "cities" (or
 * "countries" / "regions") keep exactly the access they had before the three
 * screens were merged into one tabbed page.
 */
export type LocationEntity = "countries" | "regions" | "cities";

export const LOCATIONS_MODULE = "locations";

export type LocationTab = {
  key: LocationEntity;
  label: string;
  /** Any one of these module slugs grants access to the tab. */
  modules: string[];
};

export const LOCATION_TABS: readonly LocationTab[] = [
  { key: "countries", label: "Countries", modules: [LOCATIONS_MODULE, "countries"] },
  { key: "regions", label: "Regions", modules: [LOCATIONS_MODULE, "regions"] },
  { key: "cities", label: "Cities", modules: [LOCATIONS_MODULE, "cities"] },
] as const;

export const LOCATIONS_BASE_PATH = "/locations";

/** All module slugs that grant access to at least one Locations tab. */
export const LOCATION_MODULE_SLUGS: string[] = Array.from(
  new Set(LOCATION_TABS.flatMap((t) => t.modules))
);

function normSlug(v: unknown): string {
  return String(v ?? "").trim().toLowerCase();
}

export function isLocationEntity(v: unknown): v is LocationEntity {
  return LOCATION_TABS.some((t) => t.key === v);
}

/** Tab list path, e.g. /locations/cities?country=IN */
export function locationTabPath(
  entity: LocationEntity,
  scope?: { country?: string | null; region?: string | null }
): string {
  const params = new URLSearchParams();
  if (scope?.country) params.set("country", String(scope.country).toUpperCase());
  if (scope?.region) params.set("region", String(scope.region));

  const qs = params.toString();
  return `${LOCATIONS_BASE_PATH}/${entity}${qs ? `?${qs}` : ""}`;
}

export function locationCreatePath(
  entity: LocationEntity,
  scope?: { country?: string | null; region?: string | null }
): string {
  const path = locationTabPath(entity, scope);
  const [base, qs] = path.split("?");
  return `${base}/new${qs ? `?${qs}` : ""}`;
}

export function locationEditPath(
  entity: LocationEntity,
  id: string | number,
  scope?: { country?: string | null; region?: string | null }
): string {
  const path = locationTabPath(entity, scope);
  const [base, qs] = path.split("?");
  return `${base}/edit/${encodeURIComponent(String(id))}${qs ? `?${qs}` : ""}`;
}

/** Module slugs granted to the signed-in user. */
export function currentUserModules(): string[] {
  const modules = getAuth()?.user?.modules;
  return Array.isArray(modules) ? modules.map(normSlug) : [];
}

export function canAccessLocationTab(
  entity: LocationEntity,
  modules: string[] = currentUserModules()
): boolean {
  const tab = LOCATION_TABS.find((t) => t.key === entity);
  if (!tab) return false;

  const granted = new Set(modules.map(normSlug));
  return tab.modules.some((slug) => granted.has(normSlug(slug)));
}

/** Tabs the signed-in user may actually see — empty means no Locations access. */
export function allowedLocationTabs(
  modules: string[] = currentUserModules()
): LocationTab[] {
  return LOCATION_TABS.filter((t) => canAccessLocationTab(t.key, modules));
}
